import { useMemo } from 'react';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventDesignThemingProvider } from '@shared/core/website/theming';
import { artSafeZones } from '../constants/artSafeZones';

type ArtSafeZoneResult = Readonly<{
  /** Whether an art safe zone applies (flag on + theme mapped). */
  hasSafeZone: boolean;
  /** Top/bottom padding (in `vw`) to position content within the safe band. */
  safeZoneStyles?: Readonly<{ paddingTop: string; paddingBottom: string }>;
  /** Max height of the content band, e.g. `calc(100vh - 12vw - 12vw)`. */
  safeZoneMaxHeight?: string;
}>;

/**
 * Resolves the Aloha art safe zone for the current event: enabled only when the
 * `customWebsiteArtContentPlacement` flag is on AND the event's theme has a
 * measured entry in `artSafeZones`. Shared by the default WelcomeCard and the
 * custom welcome page so both treat the welcome hero identically.
 *
 * See artSafeZones.ts for why the reserve is expressed in `vw`.
 */
export const useArtSafeZone = (): ArtSafeZoneResult => {
  const [isArtContentPlacementEnabled] = useIsFeatureEnabled({ eventFeatureFlagKey: 'customWebsiteArtContentPlacement' });
  const { eventDesign } = useEventDesignThemingProvider();
  const themeId = eventDesign?.theme?.themeId;

  return useMemo(() => {
    const safeZone = isArtContentPlacementEnabled && themeId ? (artSafeZones[themeId] ?? null) : null;
    if (!safeZone) {
      return { hasSafeZone: false };
    }
    return {
      hasSafeZone: true,
      safeZoneStyles: {
        paddingTop: `${safeZone.top}vw`,
        paddingBottom: `${safeZone.bottom}vw`,
      },
      safeZoneMaxHeight: `calc(100vh - ${safeZone.top}vw - ${safeZone.bottom}vw)`,
    };
  }, [isArtContentPlacementEnabled, themeId]);
};
